import React, { createContext, useCallback, useState, useContext } from 'react';

import api from '../services/api';
import { EndpointsAPI } from '../services/Endpoints';

interface User {
  sessionToken: string;
  agencia: string;
  conta: string;
  nome: string;
  cooperativa: string;
  escopo: string;
  roles: [];
  telas: [];
}

interface SignInCredentials {
  usuario: string;
  senha: string;
}

interface AuthDataState {
  user: User;
  sessionToken: string;
}

interface AuthContextState {
  signed: boolean;
  user: any;
  signIn: (data: SignInCredentials) => Promise<void>;
  signInExterno: (data: SignInCredentials) => Promise<void>;
  signOut: () => void;
  isAuthenticated: any;
}

const AuthContext = createContext<AuthContextState>({} as AuthContextState);

export const AuthProvider: React.FC = ({ children }) => {
  const [authData, setAuthData] = useState<AuthDataState>(() => {
    const user = localStorage.getItem('@PortalUnicred/user');
    const sessionToken = localStorage.getItem(
      '@PortalUnicred/sessionToken',
    ) as any;

    if (sessionToken && user) {
      api.defaults.headers.authorization = `Bearer ${sessionToken}`;

      return { sessionToken, user: JSON.parse(user) };
    }

    return {} as AuthDataState;
  });

  const signIn = useCallback(async ({ usuario, senha }: SignInCredentials) => {
    const responseInterno = await api.post(
      `${EndpointsAPI.usuarioInterno}?usuario=${usuario}&senha=${senha}`,
    );

    const { sessionToken } = responseInterno.data;
    const user = responseInterno.data;

    api.defaults.headers.authorization = `Bearer ${sessionToken}`;

    localStorage.setItem('@PortalUnicred/user', JSON.stringify(user));
    localStorage.setItem('@PortalUnicred/sessionToken', sessionToken);

    setAuthData({ sessionToken, user });
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@PortalUnicred/user');
    localStorage.removeItem('@PortalUnicred/sessionToken');

    api.defaults.headers.authorization = '';
    setAuthData({} as AuthDataState);
  }, []);

  const isAuthenticated = () => {
    const user = localStorage.getItem('@PortalUnicred/user');
    const sessionToken = localStorage.getItem('@PortalUnicred/sessionToken');

    if (user !== null || sessionToken !== null) {
      return true;
    }

    return false;
  };

  const signInExterno = useCallback(
    async ({ usuario, senha }: SignInCredentials) => {
      await api
        .post(
          `${EndpointsAPI.usuarioExterno}?usuario=${usuario}&senha=${senha}`,
        )
        .then(req => {
          api.defaults.headers.authorization = `Bearer ${req.data.sessionToken}`;

          localStorage.setItem('@PortalUnicred/user', JSON.stringify(req.data));
          localStorage.setItem(
            '@PortalUnicred/sessionToken',
            req.data.sessionToken,
          );

          setAuthData({ sessionToken: req.data.sessionToken, user: req.data });
        })
        .catch(err => {
          console.log(err);
        });
    },
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        signed: Boolean(authData.user),
        user: authData.user,
        signIn,
        signInExterno,
        signOut,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const authData = useContext(AuthContext);

  if (!authData) {
    throw new Error('Cannot use useAuth outside a AuthProvider');
  }

  return authData;
}

export default AuthProvider;
